<section
  #requestItemElement
  class="request-item-container"
  [class]="{
    'request-item-container-active': isActive$ | async,
  }"
  (click)="selectRequestItem()"
  [dataSelector]="dataSelector + '-container'"
>
  <div class="request-item-content" [dataSelector]="dataSelector + '-content'">
    <div class="request-item-header" [dataSelector]="dataSelector + '-header'">
      <h3 class="h4-500 color-gray-100 request-item-title" tooltipOverflow [dataSelector]="dataSelector + '-title'">
        @if ((enableReferenceId$ | async) && itemReferenceId()) {
          <span class="reference-id">{{ itemReferenceId() }} </span>
        }
        {{ requestItem().name }}
      </h3>

      <div class="request-item-header-addon">
        @if (isReadyToSubmit() && shouldShowReadyToSubmitBadge()) {
          <div class="ready-to-submit-badge me-2">
            <aiwyn-icon name="check" class="color-green-500 me-1" [height]="16" [width]="16"></aiwyn-icon>
            <span class="body-400 color-gray-500">{{ readyToSubmitBadgeLabel() }}</span>
          </div>
        }

        <div class="status-badge" [class]="requestItem().status" [dataSelector]="dataSelector + '-status-icon'">
          @switch (requestItem().status) {
            @case (ClientRequestItemStatus.OPEN) {
              <awn-badge class="me-1"></awn-badge>
              <span class="color-blue-100"> Open </span>
            }
            @case (ClientRequestItemStatus.IN_REVIEW) {
              <awn-badge class="me-1" color="yellow"></awn-badge>
              <span class="color-yellow-100"> In Review </span>
            }
            @case (ClientRequestItemStatus.REJECTED) {
              <awn-badge class="me-1" color="red"></awn-badge>
              <span class="color-red-100"> Changes Requested </span>
            }
            @case (ClientRequestItemStatus.DONE) {
              <awn-badge class="me-1" color="green"></awn-badge>
              <span class="color-green-100"> Accepted </span>
            }
          }
        </div>

        @if (enableCommentsInActivitySidebar() || requestItem().notes) {
          <div class="d-flex justify-content-center">
            <button
              andButton
              class="p-1"
              color="ghost"
              size="small"
              (click)="onShowRequestComments(); $event.stopPropagation()"
              [dataSelector]="dataSelector + '-notes-button'"
            >
              <aiwyn-icon name="message-square" [height]="16" [width]="16"></aiwyn-icon>
            </button>

            @if (unreadMessagesCount() > 0) {
              <app-unseen-dot (click)="onShowRequestComments(); $event.stopPropagation()" />
            }
          </div>
        }

        <!-- Button is restricted  -->
        @if(
          requestItem().type === RequestItemType.DOCUMENT_REQUEST &&
          requestItem().status !== ClientRequestItemStatus.DONE &&
          (isInReviewEditingEnabled() || requestItem().status !== ClientRequestItemStatus.IN_REVIEW) &&
          !readonly()
        ) {
          <button
            andButton
            class="kebab-button p-1"
            color="ghost"
            size="small"
            [showMenuIcon]="false"
            [matMenuTriggerFor]="menu"
            (click)="$event.stopPropagation()"
            [dataSelector]="dataSelector + '-menu-button'"
          >
            <aiwyn-icon name="more-horizontal" [height]="16" [width]="16"></aiwyn-icon>
          </button>
        }
      </div>
    </div>
    @let description = requestItem().description;
    @if (description) {
      <p class="color-gray-400 m-0 request-item-description" [dataSelector]="dataSelector + '-description'" [innerHTML]="description | sanitizedHtml"></p>
    }

    @if ((enableReferenceFiles$ | async) && requestItem().clientRequestItemFileReferences?.length) {
      <div class="reference-files mt-2" [dataSelector]="dataSelector + '-reference-files'">
        <aiwyn-icon name="file-text" [height]="16" [width]="16" class="color-gray-400"></aiwyn-icon>
        <span class="body-300 color-gray-400 ms-1">Reference files:</span>
        @for (fileRef of visibleReferenceFiles(); track fileRef.id; let last = $last; let index = $index) {
          <button
            class="reference-file-link"
            (click)="onReferenceFileClick(fileRef, $event)"
            [dataSelector]="dataSelector + '-reference-file-' + fileRef.id"
            tooltipOverflow
            position="above"
          >
            {{ fileRef.file.name }}
          </button>
          @if (!last || (showMoreReferenceFilesButton() && remainingFilesCount() > 0)) {
            <span class="color-gray-400">,</span>
          }
        }
        @if (showMoreReferenceFilesButton() && remainingFilesCount() > 0) {
          <button class="reference-file-link" (click)="expandReferenceFiles($event)" [dataSelector]="dataSelector + '-show-more-files'">
            +{{ remainingFilesCount() }} more
          </button>
        }
      </div>
    }

    <div class="sub-header mt-2 mb-4" [dataSelector]="dataSelector + '-sub-header'">
      <div
        class="assignee-button"
        [class]="{ 'assignee-button-disabled': requestItem().status !== ClientRequestItemStatus.OPEN }"
        tooltip="This request can't be reassigned because the assignee has already started or submitted it."
        [tooltipEnabled]="requestItem().status !== ClientRequestItemStatus.OPEN"
        (click)="updateAssignee($event)"
        [dataSelector]="dataSelector + '-assignee-button'"
      >
        <app-avatar
          [avatarInput]="avatars$ | async"
          [domain]="'contact'"
          [limit]="3"
          [showLabel]="true"
          [dataSelector]="dataSelector + '-assignee-avatar'"
          nameStyle="full"
        ></app-avatar>

        @if (requestItem().status !== ClientRequestItemStatus.OPEN) {
          <aiwyn-icon name="pencil-simple-slash" namespace="phosphor" [height]="16" [width]="16" [dataSelector]="dataSelector + '-disabled-edit-icon'"></aiwyn-icon>
        } @else if (!requestItem().assignees?.[0]?.secureLinkUsers?.length) {
          <aiwyn-icon name="edit-2" [height]="16" [width]="16" [dataSelector]="dataSelector + '-edit-icon'"></aiwyn-icon>
        } @else {
          @if (isDeleting) {
            <mat-spinner [diameter]="16" [dataSelector]="dataSelector + '-remove-secure-link-icon'"></mat-spinner>
          } @else {
            <aiwyn-icon name="rotate-ccw" [height]="16" [width]="16" [dataSelector]="dataSelector + '-remove-secure-link-icon'"></aiwyn-icon>
          }
        }
      </div>

      <div class="due-date-block" [dataSelector]="dataSelector + '-due-date'">
        <aiwyn-icon name="calendar" [height]="16" [width]="16" [dataSelector]="dataSelector + '-calendar-icon'"></aiwyn-icon>

        <span class="body-300 color-gray-100 ms-2" [dataSelector]="dataSelector + '-due-date-text'"
          >Due {{ requestItem().dueDate | date: 'MM/dd/yyyy' }}</span
        >
      </div>
    </div>

    <div class="request-item-child-content" [dataSelector]="dataSelector + '-child-content'">
      <ng-content></ng-content>
    </div>
  </div>
</section>

<mat-menu #menu="matMenu" xPosition="before">
  <div mat-menu-item (click)="openLinkFilesDialog()" [dataSelector]="dataSelector + '-link-files-option'">
    <aiwyn-icon name="file-plus" class="icon-color-gray-500 me-1" [height]="16" [width]="16"></aiwyn-icon>
    Link file(s)
  </div>
</mat-menu>
